import { createFileRoute } from "@tanstack/react-router";
import { IMAGES } from "@/lib/images";
import {
  GuideHero,
  GuideBody,
  GuideFaq,
  GuideCta,
  GuideRelated,
  GuideInlineCta,
  CheckList,
  KeyStats,
  Callout,
  ResponsiveTable,
  buildGuideHead,
  type GuideMeta,
} from "@/components/GuideLayout";

const meta: GuideMeta = {
  category: "Solfilm · Privat",
  title: "Solfilm för villa och lägenhet — komplett guide (2026)",
  tldr:
    "Solfilm för villa reducerar solvärmen med 50 % och blockerar 99 % av UV-strålningen. Lär dig vilka rum som tjänar mest, vad det kostar och om ROT-avdrag gäller. BBN Nordic — auktoriserad 3M-installatör sedan 2008.",
  readTimeMin: 9,
  heroImage: IMAGES.solfilmHero,
  path: "/guider/solfilm-villa",
  updated: "2026-07-23",
  published: "2026-06-16",
  faqs: [
    {
      q: "Är solfilm bättre än att köpa AC?",
      a: "De löser olika problem. Solfilm stoppar värme innan den kommer in — AC tar bort värme som redan är inne och drar el medan den gör det. För ett rum med ett tydligt solinstrålningsproblem är film ofta billigare både att installera och att ha i drift. Har du redan AC sänker film drifttiden märkbart.",
    },
    {
      q: "Märks solfilm utifrån?",
      a: "Prestige-filmer är spektralt selektiva och har låg yttre reflektion (<10 %). På vanligt villafönster är de i princip osynliga från gatan. Äldre silvriga reflektionsfilmer (fortfarande vanliga i butiksfönster) ser annorlunda ut — men det är inte vad vi monterar på bostäder.",
    },
    {
      q: "Kan jag filma bara de värsta fönstren?",
      a: "Ja, och det är det vanligaste upplägget. De flesta börjar med 2–4 fönster och lägger till fler vid behov. Det finns inget krav på att filma hela huset.",
    },
    {
      q: "Hur lång tid tar installationen?",
      a: "En tekniker monterar normalt ett helt hus under en dag. Du kan vara hemma under arbetet och använda rummen direkt efter — filmen är torr på ett par timmar.",
    },
    {
      q: "Fungerar solfilm på energiglas med low-e beläggning?",
      a: "Ja, men filmvalet är viktigt. På moderna 3-glasfönster med inbyggd low-e beläggning väljer vi alltid en invändig film med låg absorptionsförmåga för att undvika risken för termiska spänningar i rutan. Det är en av anledningarna till att mätning på plats är viktigt.",
    },
  ],
  related: [
    { to: "/solfilm-privat", label: "Solfilm för privatpersoner — produktöversikt" },
    { to: "/3m-prestige", label: "3M Prestige-serien — datablad" },
    { to: "/guider/solfilm-vs-persienner", label: "Solfilm eller persienner?" },
    { to: "/guider/underhall-livslangd", label: "Skötsel & livslängd" },
  ],
};

export const Route = createFileRoute("/guider/solfilm-villa")({
  head: () => buildGuideHead(meta),
  component: Page,
});

function Page() {
  return (
    <main className="bg-background">
      <GuideHero meta={meta} />
      <GuideBody>
        <KeyStats
          items={[
            { value: "−50 %", label: "Solvärme", sub: "TSER, 3M Prestige 70" },
            { value: "99 %", label: "UV-skydd", sub: "stoppar blekning" },
            { value: "3–6 °C", label: "Svalare rum", sub: "sydläge, sommar" },
            { value: "10 år", label: "Produktgaranti", sub: "via auktoriserad installatör" },
          ]}
          source="3M datablad + BBN-installationer i Skåne"
        />

        <p>
          Det börjar alltid på samma sätt. En varm julieftermiddag, solen hänger lågt i
          väster och vardagsrummet är 29 grader trots att du hållit fönstren stängda hela
          dagen. Du drar ner persiennerna, men nu är det mörkt och instängt istället. Det
          känns inte som ett hem längre — det känns som ett växthus.
        </p>
        <p>
          Det är precis den situationen de flesta av våra privatkunder befinner sig i när
          de hör av sig. Och det är en situation som solfilm löser permanent, utan att du
          behöver byta fönster, installera AC eller acceptera att rummet är oanvändbart
          halva sommaren.
        </p>
        <p>
          Den här guiden förklarar hur solfilm fungerar i en bostad, vilka rum som tjänar
          mest på det, hur du väljer rätt film och vad du kan förvänta dig av en
          installation.
        </p>

        <h2>Varför svenska bostäder overhettar på sommaren</h2>
        <p>
          Svenska hus är byggda för att samla solens värme. Det är rationellt — vi har
          långa, kalla vintrar och behöver varje gratiskalori vi kan få. Stora
          söderfönster, välvinklade takfönster och glasade uterum är en direkt konsekvens
          av det.
        </p>
        <p>
          Men under juni, juli och delar av maj och augusti vänder problemet. Samma
          egenskaper som värmer huset gratis i mars förvandlar söder- och västrummen till
          ugnar på eftermiddagen. En enda kvadratmeter södervänt glas kan ta in upp till
          800 watt solinstrålning vid sol i zenit — ungefär som en elradiator per
          fönsterruta, men utan termostat.
        </p>
        <p>
          Vanligt 2- eller 3-glasfönster filtrerar bort nästan ingenting av den energin.
          Glaset är transparent för solljusets infraröda del, som bär med sig det mesta
          av värmen, och det är den du behöver stoppa.
        </p>

        <h2>Vad solfilm faktiskt gör — och inte gör</h2>
        <p>
          Solfilm är ett tunt polyesterskikt, typiskt 50–100 mikrometer, som monteras på
          insidan av glaset med ett optiskt lim. Filmen innehåller tunna metalloxidlager
          — i 3M Prestige-seriens fall titanoxid och nickelkrom — som selektivt
          reflekterar infraröd strålning utan att påverka synligt ljus i samma
          utsträckning.
        </p>
        <p>
          Det betyder att en Prestige 70-film reflekterar bort 50 % av solvärmen men bara
          tar bort 31 % av synligt ljus. Du ser nästan ingen skillnad i rumsbelysningen,
          men rummet hålls svalare.
        </p>
        <p><strong>Vad solfilm gör:</strong></p>
        <CheckList
          items={[
            "Reducerar värmeinstrålning med 50 % (Prestige 70)",
            "Blockerar >99 % av UV-A och UV-B strålning",
            "Minskar bländning på skärmar, TV och köksbänkar",
            "Skyddar parkett, möbler och textilier från att blekna",
          ]}
        />
        <p><strong>Vad solfilm inte gör:</strong></p>
        <ul>
          <li>Mörklägger rummet (för det behöver du gardiner eller persienner)</li>
          <li>Löser problem med kondens mellan glasrutorna — det är ett tätningsproblem i glaset</li>
          <li>Ersätter isolering eller tätning av luftläckor vid fönsterkarmar</li>
        </ul>

        <GuideInlineCta />

        <h2>Vilka rum tjänar mest på solfilm?</h2>
        <p>
          Det enkla svaret: rum med stora glaspartier mot syd, sydväst och väst, där du
          faktiskt vistas under de timmar solen är som starkast.
        </p>
        <p>
          <strong>Sovrum med morgon- eller eftermiddagssol</strong> är den vanligaste
          startpunkten. En söder- eller västervänd sovrumsruta som släpper in sol från
          kl 13 gör att rummet är 27–29 grader när du ska sova. Med solfilm sjunker
          maxtemperaturen 3–5 grader, vilket gör skillnaden mellan ett rum du sover bra
          i och ett du inte kan använda.
        </p>
        <p>
          <strong>Vardagsrum med stora skjutdörrar eller panoramafönster</strong> är den
          näst vanligaste situationen. Här är problemet ofta en kombination av värme,
          bländning på TV:n och att parkett eller soffa bleknar synligt under fönstret.
          Film löser alla tre på en gång.
        </p>
        <p>
          <strong>Uterum och glasveranda</strong> är det område där effekten är allra
          mest dramatisk. Ett ickefiltrerat glastak kan höja temperaturen 10–15 grader
          jämfört med utomhus. Med solfilm på taket och de värsta sidofönstren kan
          uterummet vara 6–8 grader svalare — skillnaden mellan ett rum du faktiskt
          använder och ett du undviker från maj till september.
        </p>
        <p>
          <strong>Takfönster</strong> är en specialkategori. Solinfallet genom ett
          horisontellt takfönster är väsentligt kraftigare än genom ett vertikalt
          fönster, eftersom glaset är i rät vinkel mot solen under de timmar solen är
          som starkast. Många kunder med nyinstallerade takfönster kontaktar oss för att
          rummet under är oanvändbart på sommaren trots att det är perfekt på vintern.
        </p>
        <p>
          <strong>Hemmakontor med skärm mot ett söder- eller västerläge</strong> är ett
          problem som vi ser mer och mer. Bländningen är inte bara irriterande — den
          tvingar dig att arbeta med lägre ljusstyrka, felaktiga skärmvinklar eller
          dragna persienner och lysrörsljus. Film eliminerar bländningen utan att du
          förlorar naturligt dagsljus.
        </p>

        <h2>UV-skydd och blekning — det som sitter kvar längst</h2>
        <p>
          Värmereduktionen märks omedelbart. UV-skyddet märker du inte alls — förrän du
          lyfter upp mattan och ser skillnaden på parkettgolvet.
        </p>
        <p>
          UV-A strålning, som vanligt fönsterglas i hög grad släpper igenom, driver den
          kemiska nedbrytning av färgpigment som gör att trä bleknar, läder sprickter
          och textilier tappar sin nyans. Det sker gradvis och utan att du lägger märke
          till det dag för dag, men ett par år av riklig solexponering syns tydligt.
        </p>
        <p>
          3M Prestige-film blockerar mer än 99 % av all UV-strålning, vilket i praktiken
          stoppar UV-driven blekning. Det är den egenskapen som gör solfilm intressant
          även i rum som inte har ett värmeproblem — ett norrvästrum med dyrbar konst,
          ett sovrum med ljuskänsliga textilier, ett hobbyrum med fotoutrustning.
        </p>

        <GuideInlineCta />

        <h2>Vilken film passar din situation?</h2>
        <p>Tre filmer täcker de flesta villasituationer:</p>
        <ResponsiveTable>
          <thead>
            <tr>
              <th>Film</th>
              <th>Ljusgenomsläpp</th>
              <th>Värmereduktion (TSER)</th>
              <th>Passar bäst för</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>3M Prestige 70</td>
              <td>68 %</td>
              <td>50 %</td>
              <td>Rum där du vill ha minimal synlig förändring. Vardagsrum, sovrum med måttlig sol.</td>
            </tr>
          </tbody>
        </ResponsiveTable>
        <p className="text-sm text-muted-foreground">
          Källa: 3M datablad. TSER = Total Solar Energy Rejected.
        </p>
        <p>
          Vi rekommenderar aldrig en specifik film utan att ha sett fönstren. Glastyp,
          befintliga mellanskikt och rummets faktiska orientering påverkar utfallet mer
          än produktspecifikationen antyder. En kostnadsfri mätning på plats tar 30–45
          minuter och ger ett säkert svar.
        </p>

        <h2>Vad kostar solfilm på en villa?</h2>
        <p>
          Det exakta priset beror på fönsteryta, filmval och tillgänglighet, men för att
          ge dig en referenspunkt: de flesta villakunder vi arbetar med har 4–10 fönster
          de vill filma, med en total glasyta på 15–40 kvm. Det ger en
          installationskostnad på ungefär 8 000–25 000 kr inklusive montering och
          material.
        </p>
        <p>
          För den som har AC eller luftvärmepump som kyler huset under sommaren är
          återbetalningstiden i form av minskad energikostnad vanligtvis 5–10 år. Men de
          flesta av våra privatkunder räknar inte primärt på energi — de räknar på att
          uterum, sovrum och vardagsrum faktiskt kan användas halvåret om istället för
          att vara oanvändbara tre månader.
        </p>
        <Callout variant="tip" title="ROT-avdrag gäller">
          <p>
            Installation av solfilm på privatbostad är ROT-berättigat. Avdraget är 30 %
            av arbetskostnaden och dras direkt på fakturan — du behöver bara uppge
            personnummer och fastighetsbeteckning. I praktiken sänker ROT-avdraget din
            kostnad med ungefär 15–20 % av totalpriset.
          </p>
        </Callout>

        <GuideInlineCta />

        <h2>Praktiska saker att veta innan installation</h2>
        <p>
          <strong>Bostadsrätt:</strong> Informera din förening. Klara, neutrala
          Prestige-filmer ger minimal visuell förändring utifrån och godkänns i princip
          alltid, men det är artigt och ofta reglementenligt att meddela styrelsen i
          förväg. Vi kan hjälpa till med ett standardbrev om du vill.
        </p>
        <p>
          <strong>Härdning:</strong> Den första veckan efter montering kan du se små
          vattenbubblor eller grumliga fläckar i filmen. Det är normalt — det är
          restvatten från limet som arbetar sig ut. Bubblorna försvinner av sig själva.
          Vänta 30 dagar med våtrengöring.
        </p>
        <p>
          <strong>Skötsel:</strong> Rengör filmen som vanligt glas med milt diskmedel
          och mjuk trasa. Undvik slipande medel, ammoniak (vanligt i fönsterputs) och
          stålull — de skadar filmytan och kan ogiltigförklara garantin.
        </p>
        <p>
          <strong>Garantin följer fastigheten:</strong> Garantibeviset är kopplat till
          fönstret, inte till dig personligen. Det innebär att garantin gäller även om
          du säljer bostaden — vilket kan vara en tillgång i en försäljningssituation.
        </p>
      </GuideBody>
      <GuideFaq items={meta.faqs ?? []} />
      <GuideCta />
      <GuideRelated items={meta.related ?? []} />
    </main>
  );
}
